import { ArrayResolution, Expression, Filter } from '@fabric-ui/components';
import { PeriodBehavior } from '../../components/DatePicker/AvDateRangePicker.constants';
import { isExpr } from './filter-utils';

// @nizans @amitwitk
// TODO:
// - Add "not" operator
// - Enforce types based on filter type
// - Improve filter types (don't allow multiple conditions on same expression, etc)
// - Add "ipCondition"
// - Add "fieldCondition"
// - Fix dateCondition empty/notEmpty/equals/notEqual (BE rejects these filters)

const OP = {
  AND: 'and',
  OR: 'or',
} as const;

type Op = (typeof OP)[keyof typeof OP];
const expr = (e: Expression): Filter => ({ expression: e });

const stringExpr = (fieldName: string) =>
  ({
    equals: (v: string): Filter => expr({ fieldName, stringCondition: { equals: v } }),
    notEqual: (v: string): Filter => expr({ fieldName, stringCondition: { notEqual: v } }),
    contains: (v: string): Filter => expr({ fieldName, stringCondition: { contains: v } }),
    notContains: (v: string): Filter => expr({ fieldName, stringCondition: { notContains: v } }),
    starts: (v: string): Filter => expr({ fieldName, stringCondition: { starts: v } }),
    ends: (v: string): Filter => expr({ fieldName, stringCondition: { ends: v } }),
    in: (vals: string[]): Filter => expr({ fieldName, stringCondition: { in: { values: vals } } }),
    notIn: (vals: string[]): Filter => expr({ fieldName, stringCondition: { not_in: { values: vals } } }),
    empty: (): Filter => expr({ fieldName, stringCondition: { empty: {} } }),
    notEmpty: (): Filter => expr({ fieldName, stringCondition: { notEmpty: {} } }),
  }) as const;

const numberExpr = (fieldName: string) =>
  ({
    equals: (v: number): Filter => expr({ fieldName, numberCondition: { equals: v } }),
    notEqual: (v: number): Filter => expr({ fieldName, numberCondition: { notEqual: v } }),
    gt: (v: number): Filter => expr({ fieldName, numberCondition: { gt: v } }),
    lt: (v: number): Filter => expr({ fieldName, numberCondition: { lt: v } }),
    gte: (v: number): Filter => expr({ fieldName, numberCondition: { gte: v } }),
    lte: (v: number): Filter => expr({ fieldName, numberCondition: { lte: v } }),
    between: (from: number, to: number): Filter => expr({ fieldName, numberCondition: { between: { from, to } } }),
    empty: (): Filter => expr({ fieldName, numberCondition: { empty: {} } }),
    notEmpty: (): Filter => expr({ fieldName, numberCondition: { notEmpty: {} } }),
  }) as const;

const boolExpr = (fieldName: string) =>
  ({
    equals: (v: boolean): Filter => expr({ fieldName, boolCondition: { equals: v } }),
    empty: (): Filter => expr({ fieldName, boolCondition: { empty: {} } }),
    notEmpty: (): Filter => expr({ fieldName, boolCondition: { notEmpty: {} } }),
  }) as const;

const dateExpr = (fieldName: string) =>
  ({
    between: (from: Date, to: Date): Filter => expr({ fieldName, dateCondition: { between: { values: [from, to] } } }),
    // dateCondition empty does not works currently in the BE - but stringCondition does
    empty: (): Filter => expr({ fieldName, stringCondition: { empty: {} } }),
    // dateCondition notEmpty does not works currently in the BE - but stringCondition does
    notEmpty: (): Filter => expr({ fieldName, stringCondition: { notEmpty: {} } }),
    // dateCondition equal does not works currently in the BE - but stringCondition does
    equal: (v: string): Filter => expr({ fieldName, stringCondition: { equals: v } }),
    // dateCondition notEqual does not works currently in the BE - but stringCondition does
    notEqual: (v: string): Filter => expr({ fieldName, stringCondition: { notEqual: v } }),
    relative: (period: PeriodBehavior, value: number, unit: string): Filter =>
      expr({ fieldName, dateCondition: { relative: { periodBehaviour: period, value, unit } } }),
    before: (value: number, unit: string): Filter => dateExpr(fieldName).relative(PeriodBehavior.before, value, unit),
    last: (value: number, unit: string): Filter => dateExpr(fieldName).relative(PeriodBehavior.last, value, unit),
    next: (value: number, unit: string): Filter => dateExpr(fieldName).relative(PeriodBehavior.next, value, unit),
    previous: (value: number, unit: string): Filter => dateExpr(fieldName).relative(PeriodBehavior.previous, value, unit),
  }) as const;

const arrayExpr = (fieldName: string) => {
  const wrap = <T extends Record<string, (...args: any[]) => Filter>>(base: T, wrap: (f: Filter) => Filter): T =>
    Object.fromEntries(Object.entries(base).map(([k, fn]) => [k, (...args: any[]) => wrap(fn(...args))])) as T;

  const arrayCondition = (resolution: ArrayResolution, underlying: Filter) => {
    if (!isExpr(underlying)) throw new Error('Array underlying condition must be an expression');
    return expr({ fieldName, arrayCondition: { underlying: underlying.expression, resolution } });
  };

  const chain = (resolution: ArrayResolution) =>
    ({
      str: wrap(stringExpr(fieldName), u => arrayCondition(resolution, u)),
      num: wrap(numberExpr(fieldName), u => arrayCondition(resolution, u)),
      bool: wrap(boolExpr(fieldName), u => arrayCondition(resolution, u)),
      date: wrap(dateExpr(fieldName), u => arrayCondition(resolution, u)),
    }) as const satisfies ScalarConditions;

  return {
    all: chain(ArrayResolution.ALL),
    any: chain(ArrayResolution.ANY),
    complete: chain(ArrayResolution.COMPLETE),
  } as const;
};

type ScalarConditions = {
  str: ReturnType<typeof stringExpr>;
  num: ReturnType<typeof numberExpr>;
  bool: ReturnType<typeof boolExpr>;
  date: ReturnType<typeof dateExpr>;
};

type Conditions = ScalarConditions & {
  array: ReturnType<typeof arrayExpr>;
};

export const field = (name: string) =>
  ({
    str: stringExpr(name),
    num: numberExpr(name),
    bool: boolExpr(name),
    date: dateExpr(name),
    array: arrayExpr(name),
  }) as const satisfies Conditions;

const flatten = (op: Op, parts: Filter[]): Filter[] => parts.flatMap(p => (op in p && p[op]?.operands ? p[op].operands : [p]));

export const and = (...parts: Filter[]): Filter => ({
  and: { operands: flatten(OP.AND, parts) },
});

export const or = (...parts: Filter[]): Filter => ({
  or: { operands: flatten(OP.OR, parts) },
});
